import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, CreditCard, CalendarDays, Clock, XCircle, ShieldCheck } from "lucide-react";
import { fetchBookingPay, checkoutBookingPay, formatApiError } from "@/lib/api";

const money = (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

export default function BookingPay() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [b, setB] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const busyRef = useRef(false);

  const load = async () => {
    try { setB(await fetchBookingPay(token)); setState("ready"); }
    catch (e) { setState(e?.response?.status === 404 ? "notfound" : "ready"); if (e?.response?.status !== 404) setError(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [token]);

  const pay = async () => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError("");
    try {
      const res = await checkoutBookingPay(token);
      if (res.paid) { await load(); return; }
      window.location.assign(res.checkout_url);
    } catch (e) { setError(formatApiError(e)); busyRef.current = false; setBusy(false); }
  };

  if (state === "loading") return <div className="flex min-h-[70vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-steel-brass" /></div>;
  if (state === "notfound") return (
    <div className="mx-auto max-w-md px-6 py-28 text-center" data-testid="bookpay-notfound">
      <XCircle className="mx-auto h-12 w-12 text-steel-muted" />
      <h1 className="mt-6 font-display text-3xl font-medium text-steel-text">Payment link not found</h1>
      <p className="mt-3 text-sm text-steel-secondary">This link is invalid or expired. Please contact Steele Industries.</p>
    </div>
  );

  const paid = b.deposit_status === "paid";
  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { timeZone: b.timezone, weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });

  return (
    <div className="mx-auto max-w-md px-6 py-24" data-testid="bookpay-page">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-steel-muted">Steele Industries</p>
      <h1 className="mt-2 font-display text-3xl font-medium text-steel-text">{paid ? "Deposit received" : "Confirm your booking"}</h1>

      <div className="mt-8 border border-steel-border bg-steel-surface p-6">
        <p className="font-display text-xl text-steel-text">{b.type_name}</p>
        <p className="mt-2 flex items-center gap-2 text-sm text-steel-secondary"><CalendarDays className="h-4 w-4 text-steel-brass" /> {fmt(b.start)}</p>
        <p className="mt-1 flex items-center gap-2 text-sm text-steel-muted"><Clock className="h-4 w-4" /> {b.duration_minutes} minutes · {b.name}</p>

        <div className="mt-5 flex items-baseline justify-between border-t border-steel-border pt-5">
          <span className="text-sm text-steel-muted">{paid ? "Deposit paid" : "Deposit due"}</span>
          <strong className="font-display text-2xl text-steel-text">{money(b.deposit_amount)}</strong>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-steel-secondary">
          Your <strong className="text-steel-text">deposit</strong> is <strong className="text-steel-brass">non-refundable</strong> — but it becomes account credit toward any bill with Steele Industries.
        </p>

        {error && <p role="alert" data-testid="bookpay-error" className="mt-4 text-sm text-red-400">{error}</p>}

        {paid ? (
          <p className="mt-6 flex items-center gap-2 text-sm font-medium text-emerald-400" data-testid="bookpay-paid"><CheckCircle2 className="h-5 w-5" /> Paid — your consultation is confirmed. Check your email.</p>
        ) : (
          <>
            <button data-testid="bookpay-btn" onClick={pay} disabled={busy}
              className="mt-6 flex w-full items-center justify-center gap-2 bg-steel-accent px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-steel-accentHover disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Pay {money(b.deposit_amount)} deposit
            </button>
            <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-steel-muted"><ShieldCheck className="h-3.5 w-3.5" /> Secured by Stripe · card details never stored</p>
            {params.get("cancelled") && <p className="mt-3 text-center text-xs text-steel-muted">Checkout was closed — no charge was made.</p>}
          </>
        )}
      </div>
    </div>
  );
}
